import Route from '@ember/routing/route';
import { service } from '@ember/service';
import type PartnerService from 'tio-employee/services/partner';
import type RouterService from '@ember/routing/router';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type LpnPage from 'tio-common/models/lpn-page';

type Params = {
  page?: number;
  limit?: number;
  search?: string;
  savings?: string | string[];
  format?: string | string[];
  programs?: string | string[];
  areasOfStudy?: string | string[];
};

/**
 * Ember serializes array query params as comma-separated strings in the URL.
 * This helper converts them back to proper arrays for the store query.
 */
function toArray(value: string | string[] | undefined): string[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
}

type Meta = {
  page: {
    total: number;
  };
};

const DEFAULT_LIMIT = 50;

export default class AuthenticatedLpnIndexRoute extends Route {
  @service declare partner: PartnerService;
  @service declare router: RouterService;
  @service declare store: StoreService;

  queryParams = {
    page: { refreshModel: true },
    limit: { refreshModel: true },
    search: { refreshModel: true },
    savings: { refreshModel: true },
    format: { refreshModel: true },
    programs: { refreshModel: true },
    areasOfStudy: { refreshModel: true },
  };

  beforeModel() {
    const companySettings = this.partner.companySettings;

    if (!companySettings.lpn?.isEnabled) {
      this.router.transitionTo('authenticated.dashboard');
    }
  }

  async model(params: Params) {
    const companySettings = this.partner.companySettings;
    const page = params.page || 1;
    const limit = params.limit || DEFAULT_LIMIT;
    let meta: Meta = { page: { total: 0 } };

    const savings = toArray(params.savings);
    const format = toArray(params.format);
    const programs = toArray(params.programs);
    const areasOfStudy = toArray(params.areasOfStudy);

    const allPages = await this.store
      .query('lpn-page', {
        include: 'page,company-lpn-settings,company-lpn-settings.company-setting',
        page: {
          offset: (page - 1) * limit,
          limit,
        },
        sort: 'school-name',
        filter: {
          ...(params.search ? { search: params.search } : {}),
          ...(savings.length ? { savings: [companySettings.id, ...savings].join(',') } : {}),
          ...(format.length ? { format: [companySettings.id, ...format].join(',') } : {}),
          ...(programs.length ? { programs: [companySettings.id, ...programs].join(',') } : {}),
          ...(areasOfStudy.length
            ? { 'areas-of-study': [companySettings.id, ...areasOfStudy].join(',') }
            : {}),
        },
      })
      .then((result) => {
        meta = (result.meta as Meta) || { page: { total: 0 } };

        return result;
      });

    const lpnPages: LpnPage[] = await Promise.all(
      (allPages as LpnPage[]).map(async (page) => {
        const setting = await page.settingForCompany(companySettings.id);
        const isCompanyCreated = page.isCompanyCreated === true;

        // If this page was created by *some* company but not ours, drop it later
        const createdByOtherCompany =
          isCompanyCreated && (!setting || setting.companySetting?.id !== companySettings.id);

        const isEnabled = setting?.isEnabled ?? true;
        const employerRecommended = setting?.employerRecommended === true || isCompanyCreated;

        const badgeCappedTuition = isCompanyCreated
          ? (page.badgeCappedTuition ?? false)
          : (setting?.cappedTuition ?? page.badgeCappedTuition ?? false);

        let hasDiscount;
        if (isCompanyCreated) {
          hasDiscount = page.badgeDiscount ?? false;
        } else {
          if (setting?.hasDiscount) {
            hasDiscount = true;
          } else {
            hasDiscount = page.badgeDiscount ?? false;
          }
        }

        let discountPercent = '';

        if (hasDiscount) {
          if (isCompanyCreated) {
            discountPercent = page.discountPercent || '';
          } else {
            if (setting && setting.discountPercent > 0) {
              discountPercent = setting.discountPercent.toString();
            } else if (Number(page.discountPercent) > 0) {
              discountPercent = page.discountPercent;
            } else {
              discountPercent = '';
            }
          }

          // Always convert to string
          discountPercent = discountPercent.toString();
        }

        // Attach computed props directly to the page record
        page.setting = setting;
        page.isCompanyCreated = isCompanyCreated;
        page.isEnabled = isEnabled;
        page.employerRecommended = employerRecommended;
        page.badgeCappedTuition = badgeCappedTuition;
        page.hasDiscount = hasDiscount;
        page.discountPercent = discountPercent;
        page.createdByOtherCompany = createdByOtherCompany;

        return page;
      })
    );

    // Filter + partition
    const visible = lpnPages
      .filter((page) => !page.createdByOtherCompany) // skip partners created by another company
      .filter((page) => page.isEnabled) // skip disabled
      .sort((a, b) => a.schoolName.localeCompare(b.schoolName));

    const recommendedPartners = visible.filter((page) => page.employerRecommended);

    const allPartners = visible.filter((page) => !page.employerRecommended);

    return {
      recommendedPartners,
      allPartners,
      companySettings,
      totalItems: meta.page.total || 0,
    };
  }
}
